import React, { useState } from 'react';
import { FormikProps } from 'formik';
import { SectionTitle } from '@/components/ui/section-title';
import { ActionButton, Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FormikInput } from '@/components/ui/input';
import { CheckIcon, PencilIcon, TrashIcon, Plus, MapPin } from 'lucide-react';
import { Segurado } from '@/types/Segurado';
import { Endereco } from '@/types/Endereco';
import { FormikInputCep } from '@/components/ui/formik-input';
import { CepService } from '@/services';
import { FormikSelect } from '@/components/ui/select';
import { EstadoValues } from '@/types/enum/estado';

type SeguradoEnderecoSectionParams = {
    formikProps: FormikProps<Segurado>;
    novo: () => void;
    salvar: () => void;
    alterar: (index: number) => void;
    remover: (index: number) => void;
    loading: boolean;
    indexEditando: number|undefined;
}

const SeguradoEnderecoSection: React.FC<SeguradoEnderecoSectionParams> = ({
    novo, alterar, remover, salvar, formikProps, loading, indexEditando
}) => {
    const [loadingCep, setLoadingCep] = useState<boolean>(false);

    const onChangeCep = (value: any, formikProps: any) => {
        setLoadingCep(true);
        
        CepService.getByCep(value)
            .then(response => response.json())
            .then(result => {
                formikProps.setFieldValue(`enderecos.${indexEditando}.logradouro`, result.logradouro);
                formikProps.setFieldValue(`enderecos.${indexEditando}.numero`, '');
                formikProps.setFieldValue(`enderecos.${indexEditando}.bairro`, result.bairro);
                formikProps.setFieldValue(`enderecos.${indexEditando}.cidade`, result.localidade);
                formikProps.setFieldValue(`enderecos.${indexEditando}.uf`, result.uf);
            })
            .catch(error => {
                console.error('Erro ao consultar cep',error);
            })
            .finally(() => setLoadingCep(false));        
    };

    return (
        <>
            <SectionTitle hasButton icon={<MapPin />} cor="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                Endereços
                <Button type="button" variant="outline" size="sm" className="disabled:hidden" onClick={novo}><Plus className="mr-1.5 h-4 w-4" /> Adicionar</Button>
            </SectionTitle>
            {formikProps.values.enderecos!?.length > 0 && (                                
                <Card className='w-full overflow-hidden shadow-none'>
                    <Table className="tabela-empilhada">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Cep</TableHead>
                                <TableHead>Logradouro</TableHead>
                                <TableHead>Número</TableHead>
                                <TableHead>Bairro</TableHead>
                                <TableHead>Complemento</TableHead>
                                <TableHead>Cidade</TableHead>
                                <TableHead>UF</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {formikProps.values.enderecos!.map((endereco, index) => (
                                <TableRow key={`endereco-${index}`}>
                                    <TableCell data-label="Cep">
                                        {indexEditando===index ? (
                                            <FormikInputCep loading={loadingCep} showError={false} name={`enderecos.${index}.cep`} onCepChange={(value: string) => onChangeCep(value, formikProps)}/>
                                        ): (
                                            <>                                        
                                                {endereco.cep}
                                                {formikProps.errors.enderecos && formikProps.errors.enderecos[index] && (formikProps.errors.enderecos[index] as unknown as Endereco).cep &&(
                                                    <div className='mt-1 text-xs font-medium text-destructive'>{`${(formikProps.errors.enderecos[index] as unknown as Endereco).cep}`}</div>
                                                )}                                                                
                                            </>
                                        )}
                                    </TableCell>
                                    <TableCell data-label="Logradouro">
                                        {indexEditando===index ? (
                                            <FormikInput showError={false} name={`enderecos.${index}.logradouro`} value={formikProps.values.enderecos![indexEditando].logradouro}/>
                                        ): (
                                            <>
                                                {endereco.logradouro}
                                                {formikProps.errors.enderecos && formikProps.errors.enderecos[index] && (formikProps.errors.enderecos[index] as unknown as Endereco).logradouro &&(
                                                    <div className='mt-1 text-xs font-medium text-destructive'>{`${(formikProps.errors.enderecos[index] as unknown as Endereco).logradouro}`}</div>
                                                )}                                                                
                                            </>
                                        )}
                                    </TableCell>
                                    <TableCell data-label="Número">
                                        {indexEditando===index ? (
                                            <FormikInput showError={false} name={`enderecos.${index}.numero`} value={formikProps.values.enderecos![indexEditando].numero}/>
                                        ): (
                                            <>
                                                {endereco.numero}
                                                {formikProps.errors.enderecos && formikProps.errors.enderecos[index] && (formikProps.errors.enderecos[index] as unknown as Endereco).numero &&(
                                                    <div className='mt-1 text-xs font-medium text-destructive'>{`${(formikProps.errors.enderecos[index] as unknown as Endereco).numero}`}</div>
                                                )}                                                                
                                            </>
                                        )}
                                    </TableCell>
                                    <TableCell data-label="Bairro">
                                        {indexEditando===index ? (
                                            <FormikInput showError={false} name={`enderecos.${index}.bairro`} value={formikProps.values.enderecos![indexEditando].bairro}/>
                                        ): (
                                            <>
                                                {endereco.bairro}
                                                {formikProps.errors.enderecos && formikProps.errors.enderecos[index] && (formikProps.errors.enderecos[index] as unknown as Endereco).bairro &&(
                                                    <div className='mt-1 text-xs font-medium text-destructive'>{`${(formikProps.errors.enderecos[index] as unknown as Endereco).bairro}`}</div>
                                                )}                                                                
                                            </>
                                        )}
                                    </TableCell>
                                    <TableCell data-label="Complemento">
                                        {indexEditando===index ? (
                                            <FormikInput showError={false} name={`enderecos.${index}.complemento`} value={formikProps.values.enderecos![indexEditando].complemento}/>
                                        ): (
                                            <>
                                                {endereco.complemento}
                                                {formikProps.errors.enderecos && formikProps.errors.enderecos[index] && (formikProps.errors.enderecos[index] as unknown as Endereco).complemento &&(
                                                    <div className='mt-1 text-xs font-medium text-destructive'>{`${(formikProps.errors.enderecos[index] as unknown as Endereco).complemento}`}</div>
                                                )}                                                                
                                            </>
                                        )}
                                    </TableCell>
                                    <TableCell data-label="Cidade">
                                        {indexEditando===index ? (
                                            <FormikInput showError={false} name={`enderecos.${index}.cidade`} value={formikProps.values.enderecos![indexEditando].cidade}/>
                                        ): (
                                            <>
                                                {endereco.cidade}
                                                {formikProps.errors.enderecos && formikProps.errors.enderecos[index] && (formikProps.errors.enderecos[index] as unknown as Endereco).cidade &&(
                                                    <div className='mt-1 text-xs font-medium text-destructive'>{`${(formikProps.errors.enderecos[index] as unknown as Endereco).cidade}`}</div>
                                                )}                                                                
                                            </>
                                        )}
                                    </TableCell>
                                    <TableCell data-label="UF">
                                        {indexEditando===index ? (
                                            <FormikSelect
                                                name={`enderecos.${index}.uf`}
                                                itens={EstadoValues}
                                                getOptionLabel={value => value.descricao}
                                                getOptionValue={value => value.codigo}
                                                value={formikProps.values.enderecos![indexEditando].uf}
                                            />
                                        ): (
                                            <>
                                                {endereco.uf}
                                                {formikProps.errors.enderecos && formikProps.errors.enderecos[index] && (formikProps.errors.enderecos[index] as unknown as Endereco).uf &&(
                                                    <div className='mt-1 text-xs font-medium text-destructive'>{`${(formikProps.errors.enderecos[index] as unknown as Endereco).uf}`}</div>
                                                )}                                                                
                                            </>
                                        )}
                                    </TableCell>
                                    <TableCell className='text-right'>
                                        {index === indexEditando ? (
                                            <>
                                            <ActionButton icon={<CheckIcon className="h-3.5 w-3.5" />} label='Salvar Email' onClick={() => salvar()} />
                                            </>
                                        ) : (
                                            <>
                                            <ActionButton icon={<PencilIcon className="h-3.5 w-3.5" />} label='Editar' onClick={() => alterar(index)} />
                                            <ActionButton variant='destructive' icon={<TrashIcon className="h-3.5 w-3.5" />} label='Remover' onClick={() => remover(index)} />
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}                              
        </>
    );
}

export default SeguradoEnderecoSection;